import { Platform } from '@angular/cdk/platform';
import { AfterViewInit, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService, CosmoteIDService, AnalyticsService } from '@box-core/services';
import { LandingHero, PromoBanner } from '@box-types';
import { Subscription } from 'rxjs';
import { LandingHeroService } from './landing-hero.service';

@Component({
  selector: 'landing-hero',
  templateUrl: './landing-hero.component.html',
  styleUrls: ['./landing-hero.component.scss'],
  providers: [LandingHeroService]
})
export class LandingHeroComponent implements OnInit, OnDestroy, AfterViewInit {
  public keywords: string[];
  public color: string;
  public imageXl: string;
  public imageLg: string;
  public imageMd: string;
  public imageSm: string;
  public altImageXl: string;
  public altImageLg: string;
  public altImageMd: string;
  public altImageSm: string;
  public promoBanner: PromoBanner;
  public disabledLogin = true;

  private landingHero: LandingHero;
  private promoBannerSubscription: Subscription;
  private readonly gaCustomConfig = { source: 'landing' };

  constructor(
    private platform: Platform,
    private landingHeroService: LandingHeroService,
    private authenticationService: AuthenticationService,
    private cosmoteIDService: CosmoteIDService,
    private analyticsService: AnalyticsService
  ) {}

  @HostBinding('class') public hostClass = 'landing-hero';

  ngOnInit(): void {
    this.landingHero = this.landingHeroService.getActiveLandingHero();
    this.setLandingHeroData();
    this.setLandingPromoSubscription();
  }

  ngOnDestroy(): void {
    if (this.promoBannerSubscription) this.promoBannerSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (this.platform.isBrowser) {
      this.cosmoteIDService.setCosmoteIDSSOLogoutListener();
      this.setCosmoteIDLogin();
    }
  }

  public onConnectClick(): void {
    if (this.authenticationService.isAuthenticated) return;
    this.analyticsService.addGACustomEvent('click_login', this.gaCustomConfig);
  }

  private setLandingHeroData(): void {
    if (!this.landingHero) return;
    this.keywords = this.landingHero.keywords;
    this.color = this.landingHero.color;
    this.imageXl = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'webp-xl');
    this.imageLg = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'webp-lg');
    this.imageMd = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'webp-md');
    this.imageSm = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'webp-sm');
    this.altImageXl = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'png-xl');
    this.altImageLg = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'png-lg');
    this.altImageMd = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'png-md');
    this.altImageSm = this.landingHeroService.getLandingHeroImageSrc(this.landingHero, 'png-sm');
  }

  private setLandingPromoSubscription(): void {
    this.promoBannerSubscription = this.landingHeroService
      .getPromoBanner()
      .subscribe((promoBanner) => (this.promoBanner = promoBanner));
  }

  private setCosmoteIDLogin(): Promise<void> {
    if (!this.cosmoteIDService.scriptLoaded) {
      return this.cosmoteIDService.loadCosmoteIDScript().finally(() => {
        this.disabledLogin = false;
      });
    }
    return this.cosmoteIDService.setCosmoteIDWidget().then(() => {
      this.disabledLogin = false;
    });
  }
}
